import React, { useCallback, useEffect, useState } from 'react';
import { useAPIClientRequest } from 'c-hooks';
import ApiClient from 'c-data/apiClient';

import to from 'await-to-js';
import { useBoolean } from 'react-hanger';
import {
    Alert,
    Button,
    Checkbox,
    DialogV2,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TextAreaField,
} from 'c-components';
import {
    CircularProgress,
    IconButton,
    Paper,
    Stack,
    TableContainer,
    Typography,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import { useCommonTranslation } from 'c-translation';

type Props = {
    campaignId: number;
};

const ClassicDialog: React.FC<Props> = ({ campaignId }) => {
    const { start, error, reset } = useAPIClientRequest(
        ApiClient.Entities.Campaign.campaignClassicMediaList,
    );
    const {
        start: editMediaStart,
        error: editMediaError,
        isLoading,
        reset: editMediaReset,
    } = useAPIClientRequest(ApiClient.Entities.Campaign.campaignClassicMediaUpdate);
    const {
        start: delMediaStart,
        error: delMediaError,
        isLoading: delLoading,
        reset: delReset,
    } = useAPIClientRequest(ApiClient.Entities.Campaign.deleteCampaignClassicMedia);
    const dialogState = useBoolean(false);

    const [classicMediaList, setClassicMediaList] = useState([]);
    const [textAreaInput, setTextAreaInput] = useState('');
    const [selectedIds, setSelectedIds] = useState<number[]>([]);

    const getClassicMediaListData = useCallback(async () => {
        const [err, succ] = await to(start(campaignId));
        if (succ) {
            setClassicMediaList(succ.data.data);
        }
    }, [campaignId, start]);

    const addClassicMedia = useCallback(async () => {
        const [err, succ] = await to(
            editMediaStart(campaignId, {
                media_name: textAreaInput.split('\n').filter(x => x !== ''),
            }),
        );
        if (succ) {
            setClassicMediaList(succ.data.data);
            setTextAreaInput('');
        }
    }, [campaignId, editMediaStart, textAreaInput]);
    const deleteClassicMediaItem = useCallback(
        async (id: number) => {
            const [err, succ] = await to(
                delMediaStart(campaignId, {
                    campaign_media_ids: [id],
                }),
            );
            if (succ) {
                getClassicMediaListData();
                // setClassicMediaList(succ.data.data);
            }
        },
        [campaignId, delMediaStart, getClassicMediaListData],
    );

    const deleteSelectedClassicMedia = useCallback(async () => {
        const [err, succ] = await to(
            delMediaStart(campaignId, { campaign_media_ids: selectedIds }),
        );
        if (succ) {
            getClassicMediaListData();
            setSelectedIds([]);
        }
    }, [campaignId, delMediaStart, getClassicMediaListData, selectedIds]);

    useEffect(() => {
        getClassicMediaListData();
    }, [getClassicMediaListData]);
    const handleSelectAll = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.checked) {
            setSelectedIds(classicMediaList.map(media => media.id));
        } else {
            setSelectedIds([]);
        }
    };

    const handleSelectOne = (id: number) => {
        setSelectedIds(prev =>
            prev.includes(id) ? prev.filter(item => item !== id) : [...prev, id],
        );
    };

    const isAllSelected =
        classicMediaList.length > 0 && selectedIds.length === classicMediaList.length;
    const t = useCommonTranslation();

    return (
        <>
            <Button onClick={dialogState.setTrue}>
                {t('Modules.Main.Campaigns.CampaignSchedule.popSettings.classicMedia.title')}
            </Button>
            <DialogV2
                onClose={dialogState.setFalse}
                open={dialogState.value}
                title={t('Modules.Main.Campaigns.CampaignSchedule.popSettings.classicMedia.title')}
                maxWidth="xl"
            >
                <Stack gap={2}>
                    <Stack gap={2}>
                        <TextAreaField
                            label={t(
                                'Modules.Main.Campaigns.CampaignSchedule.popSettings.classicMedia.textAreaTitle',
                            )}
                            placeholder={t(
                                'Modules.Main.Campaigns.CampaignSchedule.popSettings.classicMedia.textAreaPlaceholder',
                            )}
                            value={textAreaInput}
                            onChange={e => setTextAreaInput(e.target.value)}
                            showCharacterCount={false}
                            helperText={`${
                                textAreaInput.split('\n').filter(str => str.trim() !== '').length
                            }`}
                        />

                        <Button
                            onClick={addClassicMedia}
                            disabled={textAreaInput.length === 0 || isLoading}
                        >
                            {isLoading ? (
                                <CircularProgress />
                            ) : (
                                t(
                                    'Modules.Main.Campaigns.CampaignSchedule.popSettings.classicMedia.submit',
                                )
                            )}
                        </Button>

                        {editMediaError && (
                            <Alert severity="error" onClose={editMediaReset}>
                                {String(editMediaError)}
                            </Alert>
                        )}
                        {error && (
                            <Alert severity="error" onClose={reset}>
                                {String(error)}
                            </Alert>
                        )}
                        {delMediaError && (
                            <Alert severity="error" onClose={delReset}>
                                {String(delMediaError)}
                            </Alert>
                        )}
                    </Stack>
                    {classicMediaList.length === 0 ? (
                        <Typography />
                    ) : (
                        <TableContainer>
                            <Table component={Paper} sx={{ width: '100%' }}>
                                <TableHead>
                                    <TableRow>
                                        <TableCell padding="checkbox">
                                            <Checkbox
                                                checked={isAllSelected}
                                                onChange={handleSelectAll}
                                                indeterminate={
                                                    selectedIds.length > 0 &&
                                                    selectedIds.length < classicMediaList.length
                                                }
                                            />
                                        </TableCell>
                                        <TableCell>
                                            {t(
                                                'Modules.Main.Campaigns.CampaignSchedule.popSettings.classicMedia.id',
                                            )}
                                        </TableCell>
                                        <TableCell>
                                            {t(
                                                'Modules.Main.Campaigns.CampaignSchedule.popSettings.classicMedia.classicMediaNames',
                                            )}
                                        </TableCell>
                                        <TableCell>
                                            {t(
                                                'Modules.Main.Campaigns.CampaignSchedule.popSettings.classicMedia.actions',
                                            )}
                                        </TableCell>
                                        <TableCell>
                                            <Button
                                                color="error"
                                                disabled={delLoading || selectedIds.length === 0}
                                                onClick={deleteSelectedClassicMedia}
                                            >
                                                {delLoading ? (
                                                    <CircularProgress />
                                                ) : (
                                                    t(
                                                        'Modules.Main.Campaigns.CampaignSchedule.popSettings.classicMedia.deleteSelected',
                                                    )
                                                )}
                                            </Button>
                                        </TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {classicMediaList.map(media => (
                                        <TableRow key={media.id} hover>
                                            <TableCell padding="checkbox">
                                                <Checkbox
                                                    checked={selectedIds.includes(media.id)}
                                                    onChange={() => handleSelectOne(media.id)}
                                                />
                                            </TableCell>
                                            <TableCell>{media.id}</TableCell>
                                            <TableCell>{media.media_name}</TableCell>
                                            <TableCell>
                                                <IconButton
                                                    disabled={delLoading}
                                                    onClick={() => deleteClassicMediaItem(media.id)}
                                                >
                                                    {delLoading ? (
                                                        <CircularProgress />
                                                    ) : (
                                                        <DeleteIcon color="error" />
                                                    )}
                                                </IconButton>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    )}
                </Stack>
            </DialogV2>
        </>
    );
};
export default ClassicDialog;
